/**
 * Authentication Context
 * Manages user authentication state and session lifecycle
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  initializeCognitoConfig,
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resendSignUp as cognitoResendSignUp,
  signOut as cognitoSignOut,
  forgotPassword as cognitoForgotPassword,
  confirmPassword as cognitoConfirmPassword,
  refreshToken as cognitoRefreshToken,
  getCurrentSession,
  getUserAttributes,
} from '@/services/cognitoService';
import { normalizeStepflowRole } from '@/utils/stepflowRoles';
import { getRestApiBearerToken, clearStepflowRestSession, setStepflowAccessToken } from '@/services/apiToken';
import {
  bootstrapWorkspaceRestSession,
  exchangeWorkspaceSession,
  fetchWorkspaces,
  setActiveTenantId,
  setLastUserWorkspaceId,
  clearWorkspaceLocalPreferences,
} from '@/services/workspaceSession';
import {
  clearPendingInviteToken,
  createWorkspace as createWorkspaceApi,
  getPendingInviteToken,
  getInviteStatus,
  shouldDropInviteToken,
  PENDING_INVITE_TOKEN_SEEN_KEY,
} from '@/services/workspaceApi';
import { fetchStripeBillingStatus, fetchOrgBillingEntitlement } from '@/services/stripeBilling';
import { withoutResolvedInvites } from '@stepflow/ui-shared/utils/inbox.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/** Paid access: Stripe subscription recorded (webhook). Set VITE_BILLING_REQUIRED=false to disable gate (local dev). */
const BILLING_REQUIRED = import.meta.env.VITE_BILLING_REQUIRED !== 'false';

/** LOCAL DEV AUTH BYPASS (doc/dev/LOCAL_DEV_STACK.md). ONLY active when VITE_LOCAL_DEV_AUTH==='1' — a
 * flag ONLY dev.sh writes to a gitignored .env.local, so production builds are never affected. It lets
 * the app render against the local moto dev-server (which does its own fake-claims auth server-side)
 * without a live Cognito session. Below, the context value is overridden with a stub user so
 * ProtectedRoute passes and consumers that read ``user.*`` work. */
const LOCAL_DEV_AUTH = import.meta.env.VITE_LOCAL_DEV_AUTH === '1';
const LOCAL_DEV_TENANT = import.meta.env.VITE_LOCAL_DEV_TENANT || 'TN-dev';
const LOCAL_DEV_USER = {
  sub: 'dev-user', email: 'dev@stepflow.local', name: 'Local Dev',
  stepflowRole: 'ADMIN', role: 'ADMIN', tenantId: LOCAL_DEV_TENANT, orgId: LOCAL_DEV_TENANT,
};

function buildUserPayload(idToken, attributes, decoded) {
  const roleRaw =
    attributes['custom:role'] || attributes['custom_role'] || decoded?.['custom:role'];
  const tenantRaw =
    attributes['custom:tenant_id'] ||
    attributes['custom_tenant_id'] ||
    decoded?.['custom:tenant_id'];
  const orgRaw =
    attributes['custom:org_id'] ||
    attributes['custom_org_id'] ||
    decoded?.['custom:org_id'];
  return {
    sub: (decoded?.sub || '').trim() || undefined,
    username: attributes.email || decoded.email || decoded['cognito:username'],
    email: attributes.email,
    name: attributes.name,
    idToken,
    stepflowRole: normalizeStepflowRole(roleRaw),
    tenantId: (tenantRaw || '').trim() || undefined,
    orgId: (orgRaw || '').trim() || undefined,
  };
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [tokenExpiryWarning, setTokenExpiryWarning] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [pendingInvites, setPendingInvitesRaw] = useState([]);
  // invites this person ACCEPTED/DECLINED in this session: a list read a moment later can still carry them (eventually
  // consistent index) — never let a stale response bring a closed invite back (INBOX.md, L143)
  const resolvedInviteTokens = useRef(new Set());
  const setPendingInvites = useCallback((next) => {
    setPendingInvitesRaw((prev) => {
      const list = typeof next === 'function' ? next(prev) : next;
      return withoutResolvedInvites(list, resolvedInviteTokens.current);
    });
  }, []);
  // LOGIN → TENANT (LOGIN-TO-TENANT.md §3): the resolver's verdict — the shell shows Join / Create / invite-only
  // BEFORE any tenant-scoped call when there's no active workspace
  const [onboarding, setOnboarding] = useState(null);
  // workspaces an admin ADDED you to (no accept step) — an Inbox FYI for this session (LOGIN-TO-TENANT.md §7)
  const [recentlyAdded, setRecentlyAdded] = useState([]);
  const noteAdded = useCallback((list) => {
    if (!(list || []).length) return;
    setRecentlyAdded((prev) => [...prev, ...list.filter((j) => !prev.some((p) => p.tenantId === j.tenantId))]);
  }, []);
  const [activeTenantId, setActiveTenantIdState] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  /**
   * Effective StepFlow role for the **active workspace** (from GET /auth/workspaces membership rows).
   * Falls back to JWT-derived user.stepflowRole when the active tenant is not in the list yet.
   */
  const workspaceRole = useMemo(() => {
    const tid = (activeTenantId || '').trim();
    if (tid && workspaces?.length) {
      const row = workspaces.find((w) => w.tenantId === tid);
      const r = row?.role;
      if (r) {
        return normalizeStepflowRole(r);
      }
    }
    return normalizeStepflowRole(user?.stepflowRole ?? 'USER');
  }, [workspaces, activeTenantId, user?.stepflowRole]);

  const refreshBillingStatus = useCallback(async () => {
    if (!BILLING_REQUIRED) {
      setHasActiveSubscription(true);
      setBillingLoading(false);
      return true;
    }
    setBillingLoading(true);
    try {
      const st = await fetchStripeBillingStatus();
      const status = (st.stripeSubscriptionStatus || '').toLowerCase();
      const canceled = status === 'canceled' || status === 'unpaid' || status === 'incomplete_expired';
      let ok = !!(st.stripeSubscriptionId && !canceled);
      if (!ok) {
        try {
          const ent = await fetchOrgBillingEntitlement();
          if (ent.hasEntitlement) {
            ok = true;
          }
        } catch {
          /* ignore */
        }
      }
      setHasActiveSubscription(ok);
      return ok;
    } catch {
      setHasActiveSubscription(false);
      return false;
    } finally {
      setBillingLoading(false);
    }
  }, []);

  /**
   * Initialize Cognito configuration on mount
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeCognitoConfig();
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Cognito:', error);
      }
    };
    initialize();
  }, []);

  /**
   * Check if user has valid session on mount
   */
  useEffect(() => {
    if (!initialized) return;

    const checkAuth = async () => {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path === '/callback' || path.endsWith('/callback')) {
        setLoading(false);
        return;
      }
      try {
        const session = await getCurrentSession();
        if (session.isValid()) {
          const idToken = session.getIdToken().getJwtToken();
          const decoded = jwtDecode(idToken);
          const attributes = await getUserAttributes();
          const userData = buildUserPayload(idToken, attributes, decoded);
          const boot = await bootstrapWorkspaceRestSession({
            idToken,
            apiBase: API_BASE_URL,
            cognitoTenantId: userData.tenantId,
          });
          setWorkspaces(boot.workspaces || []);
          setPendingInvites(boot.pendingInvites || []);
          setOnboarding(boot.onboarding || null);
          noteAdded(boot.justAdded);
          setActiveTenantIdState(boot.tenantId || '');
          setUser({
            ...userData,
            stepflowRole: boot.role
              ? normalizeStepflowRole(boot.role)
              : userData.stepflowRole,
            tenantId: boot.tenantId || userData.tenantId,
            orgId: boot.orgId || userData.orgId,
          });
          await refreshBillingStatus();
        }
      } catch (error) {
        console.log('No valid session found');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [initialized, refreshBillingStatus]);

  const switchWorkspace = useCallback(
    async (tenantId) => {
      if (!user?.idToken || !tenantId) {
        return { success: false, error: 'Not signed in' };
      }
      try {
        const data = await exchangeWorkspaceSession(user.idToken, tenantId, API_BASE_URL);
        setStepflowAccessToken(data.accessToken);
        // Billing status is tenant-scoped via the REST token. Show the gate spinner and avoid one frame
        // where workspaceRole is OWNER but hasActiveSubscription is still from the previous tenant.
        if (BILLING_REQUIRED) {
          setBillingLoading(true);
        }
        setActiveTenantId(tenantId);
        setActiveTenantIdState(tenantId);
        const fetched = await fetchWorkspaces(user.idToken, API_BASE_URL);
        const list = fetched.workspaces || [];
        setWorkspaces(list);
        setPendingInvites(fetched.pendingInvites || []);
        setOnboarding(fetched.onboarding || null);
        noteAdded(fetched.justAdded);
        const row = list.find((w) => (w.tenantId || '').trim() === (tenantId || '').trim());
        const wr = normalizeStepflowRole(row?.role || data.role || 'USER');
        setUser((prev) =>
          prev
            ? {
                ...prev,
                stepflowRole: wr,
                tenantId,
                orgId: (data.orgId || '').trim() || prev.orgId,
              }
            : prev
        );
        await refreshBillingStatus();
        setLastUserWorkspaceId(tenantId);
        return { success: true, workspaceRole: wr };
      } catch (e) {
        console.error('switchWorkspace', e);
        if (BILLING_REQUIRED) {
          setBillingLoading(false);
        }
        return { success: false, error: e.message };
      }
    },
    [user?.idToken, refreshBillingStatus]
  );

  /** Remove a pending invite from in-memory list after accept/decline so the banner updates without reload. */
  const dismissPendingInviteByToken = useCallback((token) => {
    const t = (token || '').trim();
    if (!t) return;
    resolvedInviteTokens.current.add(t);
    setPendingInvites((prev) => (prev || []).filter((p) => (p.token || '').trim() !== t));
  }, [setPendingInvites]);

  /**
   * Drop stale sessionStorage invite token when the server used to list it in pendingInvites
   * (email-matched invites) but no longer does — e.g. after accept/join or revoke.
   * Link-only invites (no email) never appear in pendingInvites; we only clear on explicit accept/decline.
   */
  useEffect(() => {
    if (!user) return;
    const tok = getPendingInviteToken().trim();
    if (!tok) return;
    const match = (pendingInvites || []).some((p) => (p.token || '').trim() === tok);
    try {
      if (match) {
        sessionStorage.setItem(PENDING_INVITE_TOKEN_SEEN_KEY, '1');
        return;
      }
      const wasSeen = sessionStorage.getItem(PENDING_INVITE_TOKEN_SEEN_KEY) === '1';
      if (wasSeen) {
        clearPendingInviteToken();
      }
    } catch {
      /* ignore */
    }
  }, [user, pendingInvites]);

  // A stored invite link that was already USED (e.g. accepted in another browser), expired, or is addressed to someone
  // else would keep "You opened an invitation link" up forever — ask the server once per sign-in and drop it.
  const [inviteLinkChecked, setInviteLinkChecked] = useState(0);
  useEffect(() => {
    if (!user) return;
    const tok = getPendingInviteToken().trim();
    if (!tok) return;
    let cancelled = false;
    const bearer = getRestApiBearerToken();
    if (!bearer) return;
    getInviteStatus(API_BASE_URL, bearer, tok).then((st) => {
      if (cancelled || !shouldDropInviteToken(st)) return;
      clearPendingInviteToken();
      setInviteLinkChecked((n) => n + 1);      // re-render the banner (it reads sessionStorage)
    });
    return () => { cancelled = true; };
  }, [user?.sub, activeTenantId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshWorkspaces = useCallback(async () => {
    if (!user?.idToken) return;
    try {
      const fetched = await fetchWorkspaces(user.idToken, API_BASE_URL);
      const list = fetched.workspaces || [];
      setWorkspaces(list);
      setPendingInvites(fetched.pendingInvites || []);
      setOnboarding(fetched.onboarding || null);
      noteAdded(fetched.justAdded);
      setUser((prev) => {
        if (!prev) return prev;
        const tid = (activeTenantId || '').trim();
        const row = list.find((w) => w.tenantId === tid);
        if (row?.role) {
          return { ...prev, stepflowRole: normalizeStepflowRole(row.role) };
        }
        return prev;
      });
    } catch (e) {
      console.warn('refreshWorkspaces', e);
    }
  }, [user?.idToken, activeTenantId]);

  const createWorkspace = useCallback(
    async (name) => {
      if (!user?.idToken) {
        throw new Error('Not signed in');
      }
      const token = getRestApiBearerToken();
      if (!token) throw new Error('Not authenticated');
      const data = await createWorkspaceApi(API_BASE_URL, token, name);
      const tid = data.workspace?.tenantId;
      if (!tid) throw new Error('Invalid server response');
      await switchWorkspace(tid);
      return data.workspace;
    },
    [user?.idToken, switchWorkspace]
  );

  /**
   * Monitor token expiry and show renewal popup
   */
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiry = () => {
      try {
        const idToken = localStorage.getItem('idToken');
        if (!idToken) return;

        const decoded = jwtDecode(idToken);
        const expiryTime = decoded.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;

        // Show warning 1 minute before expiry
        if (timeUntilExpiry > 0 && timeUntilExpiry <= 60000 && !tokenExpiryWarning) {
          setTokenExpiryWarning(true);
        }

        // Auto logout if expired
        if (timeUntilExpiry <= 0) {
          handleSignOut();
        }
      } catch (error) {
        console.error('Error checking token expiry:', error);
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkTokenExpiry, 10000);
    return () => clearInterval(interval);
    // ``user`` is the whole object and gets replaced whenever any field on
    // it is patched (workspace switch, stepflowRole refresh, …) which would
    // otherwise tear down + re-arm this 10s interval on every such change
    // and re-broadcast a re-render to every Auth consumer. Use the stable
    // sub claim instead.
  }, [user?.sub, tokenExpiryWarning]);

  /**
   * Sign in user
   */
  const signIn = async (username, password) => {
    try {
      const result = await cognitoSignIn(username, password);
      const decoded = jwtDecode(result.idToken);
      const attributes = await getUserAttributes();

      const userData = buildUserPayload(result.idToken, attributes, decoded);
      const boot = await bootstrapWorkspaceRestSession({
        idToken: result.idToken,
        apiBase: API_BASE_URL,
        cognitoTenantId: userData.tenantId,
      });
      setWorkspaces(boot.workspaces || []);
      setPendingInvites(boot.pendingInvites || []);
      setOnboarding(boot.onboarding || null);
      noteAdded(boot.justAdded);
      setActiveTenantIdState(boot.tenantId || '');
      const merged = {
        ...userData,
        stepflowRole: boot.role
          ? normalizeStepflowRole(boot.role)
          : userData.stepflowRole,
        tenantId: boot.tenantId || userData.tenantId,
        orgId: boot.orgId || userData.orgId,
      };
      setUser(merged);
      const paid = await refreshBillingStatus();
      return { success: true, user: merged, hasActiveSubscription: paid };
    } catch (error) {
      console.error('Sign in error:', error);
      // Surface the Cognito-side error code (e.g. ``UserNotConfirmedException``)
      // so callers can dispatch on it — losing the code means we can't tell
      // "wrong password" from "you never verified" and the UX dead-ends.
      return {
        success: false,
        error: error.message,
        errorCode: error.code || error.name || '',
      };
    }
  };

  /**
   * Sign up new user
   */
  const signUp = async (email, password, name) => {
    try {
      await cognitoSignUp(email, password, name);
      return { success: true };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Confirm sign up with verification code
   */
  const confirmSignUp = async (username, code) => {
    try {
      await cognitoConfirmSignUp(username, code);
      return { success: true };
    } catch (error) {
      console.error('Confirm sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Resend the email verification code for an UNCONFIRMED user.
   */
  const resendSignUp = async (username) => {
    try {
      await cognitoResendSignUp(username);
      return { success: true };
    } catch (error) {
      console.error('Resend sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Sign out user
   */
  const handleSignOut = useCallback(() => {
    cognitoSignOut();
    clearStepflowRestSession();
    clearPendingInviteToken();
    clearWorkspaceLocalPreferences();
    setUser(null);
    setWorkspaces([]);
    resolvedInviteTokens.current = new Set();     // a new person signs in → nothing carried over
    setPendingInvites([]);
    setActiveTenantIdState('');
    setTokenExpiryWarning(false);
    setHasActiveSubscription(false);
    setBillingLoading(false);
  }, []);

  /**
   * Forgot password
   */
  const forgotPassword = async (username) => {
    try {
      await cognitoForgotPassword(username);
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Confirm new password
   */
  const confirmPassword = async (username, code, newPassword) => {
    try {
      await cognitoConfirmPassword(username, code, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Confirm password error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Refresh authentication token
   */
  /**
   * After Hosted UI redirect: Cognito session already established in cognitoService.
   * Mirrors password signIn bootstrap (workspace + billing).
   */
  const finalizeOAuthSignIn = useCallback(async () => {
    try {
      setStepflowAccessToken(null);
      let session = await getCurrentSession();
      if (!session.isValid()) {
        return { success: false, error: 'Invalid session' };
      }
      let idToken = session.getIdToken().getJwtToken();
      let decoded = jwtDecode(idToken);
      let attributes = await getUserAttributes();
      const tenantFromAttrs =
        (attributes['custom:tenant_id'] ||
          attributes['custom_tenant_id'] ||
          decoded?.['custom:tenant_id'] ||
          '').trim();
      // PostAuthentication may set custom attributes after the first id token is minted; refresh once.
      if (!tenantFromAttrs) {
        await cognitoRefreshToken();
        session = await getCurrentSession();
        if (!session.isValid()) {
          return { success: false, error: 'Invalid session' };
        }
        idToken = session.getIdToken().getJwtToken();
        decoded = jwtDecode(idToken);
        attributes = await getUserAttributes();
      }
      const userData = buildUserPayload(idToken, attributes, decoded);
      const boot = await bootstrapWorkspaceRestSession({
        idToken,
        apiBase: API_BASE_URL,
        cognitoTenantId: userData.tenantId,
      });
      setWorkspaces(boot.workspaces || []);
      setPendingInvites(boot.pendingInvites || []);
      setOnboarding(boot.onboarding || null);
      noteAdded(boot.justAdded);
      setActiveTenantIdState(boot.tenantId || '');
      const merged = {
        ...userData,
        stepflowRole: boot.role
          ? normalizeStepflowRole(boot.role)
          : userData.stepflowRole,
        tenantId: boot.tenantId || userData.tenantId,
        orgId: boot.orgId || userData.orgId,
      };
      setUser(merged);
      const paid = await refreshBillingStatus();
      return { success: true, user: merged, hasActiveSubscription: paid };
    } catch (error) {
      console.error('OAuth finalize error:', error);
      return { success: false, error: error.message };
    }
  }, [refreshBillingStatus]);

  const refreshAuthToken = async () => {
    try {
      await cognitoRefreshToken();
      setTokenExpiryWarning(false);
      const session = await getCurrentSession();
      if (session.isValid()) {
        const idToken = session.getIdToken().getJwtToken();
        const decoded = jwtDecode(idToken);
        const attributes = await getUserAttributes();
        const userData = buildUserPayload(idToken, attributes, decoded);
        const boot = await bootstrapWorkspaceRestSession({
          idToken,
          apiBase: API_BASE_URL,
          cognitoTenantId: userData.tenantId,
        });
        setWorkspaces(boot.workspaces || []);
        setPendingInvites(boot.pendingInvites || []);
        setOnboarding(boot.onboarding || null);
        noteAdded(boot.justAdded);
        setActiveTenantIdState(boot.tenantId || '');
        setUser({
          ...userData,
          stepflowRole: boot.role
            ? normalizeStepflowRole(boot.role)
            : userData.stepflowRole,
          tenantId: boot.tenantId || userData.tenantId,
          orgId: boot.orgId || userData.orgId,
        });
        await refreshBillingStatus();
      }
      return { success: true };
    } catch (error) {
      console.error('Token refresh error:', error);
      handleSignOut();
      return { success: false, error: error.message };
    }
  };

  /**
   * Handle token renewal from popup
   */
  const handleTokenRenewal = async (renew) => {
    if (renew) {
      await refreshAuthToken();
    } else {
      handleSignOut();
    }
  };

  // the ACTIVE workspace's kind (who pays): only a CONSUMER workspace goes through Stripe (LOGIN-TO-TENANT.md §1)
  const activeWorkspace = (workspaces || []).find((w) => (w.tenantId || '') === (activeTenantId || '')) || null;
  const activeWorkspaceKind = activeWorkspace?.kind || '';
  const activeStripeGated = activeWorkspace ? activeWorkspace.stripeGated !== false : true;
  const value = {
    user,
    inviteLinkChecked,
    onboarding,
    recentlyAdded,
    dismissRecentlyAdded: (tid) => setRecentlyAdded((p) => p.filter((j) => j.tenantId !== tid)),
    activeWorkspaceKind,
    activeStripeGated,
    loading,
    initialized,
    tokenExpiryWarning,
    workspaces,
    pendingInvites,
    activeTenantId,
    /** Role in the active workspace (membership); use for tenant-scoped UI instead of user.stepflowRole alone */
    workspaceRole,
    billingLoading,
    hasActiveSubscription,
    refreshBillingStatus,
    switchWorkspace,
    refreshWorkspaces,
    dismissPendingInviteByToken,
    createWorkspace,
    getRestApiBearerToken,
    signIn,
    signUp,
    confirmSignUp,
    resendSignUp,
    signOut: handleSignOut,
    forgotPassword,
    confirmPassword,
    refreshAuthToken,
    handleTokenRenewal,
    finalizeOAuthSignIn,
  };

  // LOCAL DEV: override with a stub session so the app renders against the local backend without a
  // live Cognito login. Prod-safe — gated on VITE_LOCAL_DEV_AUTH (only dev.sh sets it).
  const effectiveValue = LOCAL_DEV_AUTH
    ? {
        ...value,
        user: LOCAL_DEV_USER,
        loading: false,
        initialized: true,
        hasActiveSubscription: true,
        workspaceRole: 'OWNER',
        activeTenantId: LOCAL_DEV_TENANT,
        getRestApiBearerToken: async () => 'dev-local-token',
      }
    : value;

  return <AuthContext.Provider value={effectiveValue}>{children}</AuthContext.Provider>;
};
